import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * NATS debug injector — on-screen panel.
 *
 * A self-contained, floating popup for pushing a message onto a subject so it
 * flows through `processMessage` and updates the Redux store exactly like a real
 * NATS message. Renders nothing until opened via `window.__natsinjector.open()`,
 * so it has no footprint otherwise.
 *
 * Launch from the browser console:
 *   __natsinjector.open()                         // show the panel, then paste
 *   __natsinjector.inject('lh.<...>', { ... })    // inject without the UI
 *   __natsinjector.getState()                     // copy a real payload to tweak
 */
import React, { useMemo, useState, useSyncExternalStore } from 'react';
import styled from 'styled-components';
import { natsDebugController } from './controller';
// Subjects the spaces-slice reducer understands (see processMessage). These are
// just quick-fill hints — any subject string is allowed.
const SUBJECT_HINTS = ['lh', 'signageAnalytical', 'events'];
// Convenience prefills that copy a live slice value into the payload box, so a
// real message can be cloned and tweaked instead of hand-written.
const STATE_PREFILLS = [
    { label: 'location (lh)', key: 'location' },
    { label: 'building', key: 'building' },
    { label: 'floor', key: 'floor' },
    { label: 'room', key: 'room' },
    { label: 'eventMessage (events)', key: 'eventMessage' },
];
const useControllerOpen = () => useSyncExternalStore(natsDebugController.subscribe, () => natsDebugController.isOpen(), () => false);
const useControllerHistory = () => useSyncExternalStore(natsDebugController.subscribe, () => natsDebugController.getHistory(), () => []);
const NatsDebugPanel = () => {
    const isOpen = useControllerOpen();
    const history = useControllerHistory();
    const [subject, setSubject] = useState('');
    const [payloadText, setPayloadText] = useState('{\n  \n}');
    const [error, setError] = useState(null);
    const [flash, setFlash] = useState(null);
    const parsedPreview = useMemo(() => {
        if (payloadText.trim() === '')
            return { ok: true, value: null };
        try {
            return { ok: true, value: JSON.parse(payloadText) };
        }
        catch (e) {
            return { ok: false, message: e.message };
        }
    }, [payloadText]);
    const showFlash = (message) => {
        setFlash(message);
        window.setTimeout(() => setFlash(null), 1800);
    };
    const handleFormat = () => {
        if (!parsedPreview.ok) {
            setError(`Invalid JSON: ${parsedPreview.message}`);
            return;
        }
        setError(null);
        setPayloadText(JSON.stringify(parsedPreview.value, null, 2));
    };
    const handlePrefill = (key) => {
        const spaces = natsDebugController.getSpacesState();
        const value = spaces ? spaces[key] : undefined;
        setPayloadText(JSON.stringify(value !== null && value !== void 0 ? value : null, null, 2));
        setError(null);
        showFlash(`Loaded current "${key}"`);
    };
    const handleInject = () => {
        if (subject.trim() === '') {
            setError('Subject is required.');
            return;
        }
        if (!parsedPreview.ok) {
            setError(`Invalid JSON: ${parsedPreview.message}`);
            return;
        }
        try {
            natsDebugController.inject(subject.trim(), parsedPreview.value);
            setError(null);
            showFlash(`Injected on "${subject.trim()}"`);
        }
        catch (e) {
            setError(e.message);
        }
    };
    const replayFromHistory = (record) => {
        var _a;
        setSubject(record.subject);
        setPayloadText(JSON.stringify((_a = record.payload) !== null && _a !== void 0 ? _a : null, null, 2));
        setError(null);
    };
    if (!isOpen)
        return null;
    return (_jsxs(Root, Object.assign({ role: "dialog", "aria-label": "NATS debug injector" }, { children: [_jsxs(Header, { children: [_jsx(Title, { children: "NATS Debug Injector" }), _jsx(CloseButton, Object.assign({ type: "button", "aria-label": "Close NATS debug injector", onClick: () => natsDebugController.setOpen(false) }, { children: "\u00D7" }))] }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Subject" }), _jsx(TextInput, { value: subject, spellCheck: false, placeholder: "e.g. lh.<location-id>", onChange: (e) => setSubject(e.target.value) }), _jsx(ChipRow, { children: SUBJECT_HINTS.map((hint) => (_jsx(Chip, Object.assign({ type: "button", onClick: () => setSubject(hint) }, { children: hint }), hint))) }), _jsxs(RowBetween, { children: [_jsx(FieldLabel, { children: "Payload (JSON)" }), _jsx(SmallButton, Object.assign({ type: "button", onClick: handleFormat }, { children: "Format" }))] }), _jsx(TextArea, { value: payloadText, spellCheck: false, onChange: (e) => setPayloadText(e.target.value) }), _jsx(FieldLabel, { children: "Prefill payload from current store" }), _jsx(ChipRow, { children: STATE_PREFILLS.map((prefill) => (_jsx(Chip, Object.assign({ type: "button", onClick: () => handlePrefill(prefill.key) }, { children: prefill.label }), prefill.key))) }), error && _jsx(ErrorText, { children: error }), flash && _jsx(FlashText, { children: flash }), _jsx(PushButton, Object.assign({ type: "button", onClick: handleInject }, { children: "Push message" })), history.length > 0 && (_jsxs(_Fragment, { children: [_jsx(FieldLabel, { children: "Recent injections" }), _jsx(HistoryList, { children: history.map((record, index) => (_jsxs(HistoryItem, Object.assign({ type: "button", title: "Click to reload into the form", onClick: () => replayFromHistory(record) }, { children: [_jsx(HistorySubject, { children: record.subject }), _jsx(HistoryTime, { children: new Date(record.at).toLocaleTimeString() })] }), `${record.at}-${index}`))) })] }))] })] })));
};
export default NatsDebugPanel;
const Root = styled.div `
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2147483000;
  width: 380px;
  max-width: calc(100vw - 32px);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: #14171a;
  color: #e6e9ec;
  border: 1px solid #2f353b;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  overflow: hidden;
`;
const Header = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #1c2126;
  border-bottom: 1px solid #2f353b;
  cursor: default;
`;
const Title = styled.div `
  font-weight: 600;
  letter-spacing: 0.02em;
`;
const CloseButton = styled.button `
  border: none;
  background: transparent;
  color: #9aa4ad;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;

  &:hover {
    color: #fff;
  }
`;
const Body = styled.div `
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const FieldLabel = styled.label `
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b959e;
`;
const RowBetween = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const TextInput = styled.input `
  width: 100%;
  box-sizing: border-box;
  background: #0f1214;
  color: #e6e9ec;
  border: 1px solid #2f353b;
  border-radius: 6px;
  padding: 8px 10px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: #00bceb;
  }
`;
const TextArea = styled.textarea `
  width: 100%;
  box-sizing: border-box;
  min-height: 140px;
  resize: vertical;
  background: #0f1214;
  color: #e6e9ec;
  border: 1px solid #2f353b;
  border-radius: 6px;
  padding: 8px 10px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: #00bceb;
  }
`;
const ChipRow = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const Chip = styled.button `
  border: 1px solid #2f353b;
  background: #1c2126;
  color: #c7ced4;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    border-color: #00bceb;
    color: #fff;
  }
`;
const SmallButton = styled.button `
  border: 1px solid #2f353b;
  background: #1c2126;
  color: #c7ced4;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    border-color: #00bceb;
    color: #fff;
  }
`;
const PushButton = styled.button `
  margin-top: 4px;
  border: none;
  background: #00bceb;
  color: #06222b;
  font-weight: 600;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #33cbef;
  }
`;
const ErrorText = styled.div `
  color: #ff6b6b;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
`;
const FlashText = styled.div `
  color: #4ade80;
  font-size: 12px;
`;
const HistoryList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 140px;
  overflow-y: auto;
`;
const HistoryItem = styled.button `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  border: 1px solid #2f353b;
  background: #0f1214;
  color: #c7ced4;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;

  &:hover {
    border-color: #00bceb;
  }
`;
const HistorySubject = styled.span `
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const HistoryTime = styled.span `
  color: #7b858d;
  font-size: 10px;
  flex-shrink: 0;
`;
